import { BrowserRouter, Route } from 'react-router-dom';
import { Admin, defaultTheme, defaultDarkTheme, CustomRoutes, Resource } from 'react-admin';
import polyglotI18nProvider from 'ra-i18n-polyglot';
import englishMessages from 'ra-language-english';
import { raFormLayoutLanguageEnglish } from '@react-admin/ra-form-layout';
import Dashboard from './Components/Dashboard';
import { AuthProvider, DataProvider } from './DataProvider';
import * as AffiliateNetwork from './Resources/AffiliateNetwork';
import * as ActionLog from './Resources/ActionLog';
import { ActionLogsByUserPage } from './Resources/ActionLog/Reports';
import * as BillingItem from './Resources/BillingItems';
import * as Category from './Resources/Category';
import * as Bid from './Resources/Bid';
import * as Click from './Resources/Click';
import {
  ClicksByMerchantPage,
  ClicksByReferrerPage,
  ClicksSummaryPage,
} from './Resources/Click/Reports';
import * as Feed from './Resources/Feed';
import * as Manufacturer from './Resources/Manufacturer';
import * as MappingRule from './Resources/MappingRules';
import * as Subscription from './Resources/Subscription';
import * as TrackedErrorIssues from './Resources/TrackedErrorIssues';
import * as Merchant from './Resources/Merchant';
import { MerchantsOfferSummaryPage } from './Resources/Merchant/Reports';
import * as Offer from './Resources/Offer';
import * as Product from './Resources/Product';
import * as Region from './Resources/Region';
import * as Registration from './Resources/Registration';
import * as Scan from './Resources/Scan';
import Layout from './Components/Layout';
import Preferences from './CustomRoutes/Preferences';
import Login from './CustomRoutes/Login';
import { ReactElement } from 'react';
import MappingErrorReport from './Resources/Scan/MappingErrorReport';

const i18nProvider = polyglotI18nProvider(
  () => ({ ...englishMessages, ...raFormLayoutLanguageEnglish }),
  'en',
);

export default function App(): ReactElement {
  const apiUrl = import.meta.env.VITE_API_URL;

  return (
    <BrowserRouter>
      <Admin
        authProvider={AuthProvider}
        dataProvider={DataProvider(apiUrl, AuthProvider)}
        theme={defaultTheme}
        darkTheme={defaultDarkTheme}
        dashboard={Dashboard}
        disableTelemetry
        i18nProvider={i18nProvider}
        layout={Layout}
        loginPage={Login}
        requireAuth
      >
        <Resource name="action_logs" list={ActionLog.List} options={{ label: 'Action Log' }} />
        <Resource name="action_log_changes" />
        <Resource name="action_logs_by_users" />
        <Resource
          name="categories"
          create={Category.Create}
          edit={Category.Edit}
          list={Category.List}
        />
        <Resource name="labels" />
        <Resource name="label_values" />
        <Resource name="products" create={Product.Create} edit={Product.Edit} list={Product.List} />
        <Resource name="product_labels" />
        <Resource name="offers" edit={Offer.Edit} list={Offer.List} />
        <Resource
          name="bids"
          create={Bid.Create}
          edit={Bid.Edit}
          list={Bid.List}
          show={Bid.Show}
          options={{ label: 'Bidding' }}
        />
        <Resource
          name="merchants"
          create={Merchant.Create}
          edit={Merchant.Edit}
          list={Merchant.List}
          recordRepresentation={(record) => `${record.attributes.merchant_name}`}
        />
        <Resource name="feeds" create={Feed.Create} edit={Feed.Edit} list={Feed.List} />
        <Resource
          name="registrations"
          create={Registration.Create}
          edit={Registration.Edit}
          list={Registration.List}
        />
        <Resource
          name="manufacturers"
          create={Manufacturer.Create}
          edit={Manufacturer.Edit}
          list={Manufacturer.List}
        />
        <Resource name="regions" create={Region.Create} edit={Region.Edit} list={Region.List} />
        <Resource
          name="affiliate_networks"
          create={AffiliateNetwork.Create}
          edit={AffiliateNetwork.Edit}
          list={AffiliateNetwork.List}
          options={{ label: 'Affiliate Networks' }}
        />
        <Resource
          name="mapping_rules"
          options={{ label: 'Mapping Rules' }}
          create={MappingRule.Create}
          edit={MappingRule.Edit}
          list={MappingRule.List}
        />
        <Resource name="clicks" list={Click.List} options={{ label: 'Clicks' }} />
        <Resource
          name="billing_items"
          list={BillingItem.List}
          edit={BillingItem.Edit}
          options={{ label: 'Billing Items' }}
        />
        <Resource name="scans" list={Scan.List} show={Scan.Show} options={{ label: 'Scans' }} />
        <Resource
          name="subscriptions"
          list={Subscription.List}
          options={{ label: 'Price Alerts' }}
        />
        <Resource
          name="tracked_error_issues"
          list={TrackedErrorIssues.List}
          show={TrackedErrorIssues.Show}
          options={{ label: 'Errors' }}
        />
        <Resource name="tracked_error_occurrences" />
        <CustomRoutes>
          <Route path="/reports/action-logs/by-user" element={<ActionLogsByUserPage />} />
          <Route path="/reports/clicks/summary" element={<ClicksSummaryPage />} />
          <Route path="/reports/clicks/by-merchant" element={<ClicksByMerchantPage />} />
          <Route path="/reports/clicks/by-referrer" element={<ClicksByReferrerPage />} />
          <Route path="/reports/merchants/offer-summary" element={<MerchantsOfferSummaryPage />} />
          <Route path="/preferences" element={<Preferences />} />
          <Route path="/mapping_errors/:scanId/:merchantId" element={<MappingErrorReport />} />
        </CustomRoutes>
      </Admin>
    </BrowserRouter>
  );
}
